import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';

import { TpSlDragIntent } from './tp-sl-drag-intent';

/**
 * Flow A 드래그 라인의 시각 표현(라벨 텍스트 + kind별 색 스타일).
 *
 * createOrderLine은 charting_library 런타임 색 문자열만 받으므로(Panda 토큰 미해석),
 * 디자인 값을 여기 상수로 둔다. (zone-fill 모듈과 동일한 이유로 분리해 테스트 가능하게 한다.)
 */

// ──────────────────────────────────────────────────────────────────────────
// 라벨: 2분할 (좌측 본문 + 우측 값 박스)
// ──────────────────────────────────────────────────────────────────────────

export type ExpectedPnlLabel = {
  /** 좌측 본문 (예: "Expected profit"). */
  text: string;
  /** 우측 값 박스 (예: "+100 USDT"). */
  value: string;
};

/**
 * @param quoteCurrency 심볼의 quote currency 표기(예: 'USDT', 'USD1'). 하드코딩하지 말고 `tv.quoteCurrency`를 넘길 것.
 */
export function buildExpectedPnlLabel(expectedPnl: number, quoteCurrency: string): ExpectedPnlLabel {
  const isProfit = expectedPnl >= 0;
  const sign = isProfit ? '+' : '-';
  const amount = numberFormatter(Num(expectedPnl).abs().toFixed(UsdtPrecision));

  return {
    text: isProfit ? 'Expected profit' : 'Expected loss',
    value: `${sign}${amount} ${quoteCurrency}`,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// 색 스타일: TP(이익)·SL(손실) 모두 어두운 배경 + 색 보더 + 흰 텍스트. 보더/라인 색만 다르다
// (TP=녹색, SL=빨강).
// ──────────────────────────────────────────────────────────────────────────

const PROFIT_COLOR = '#0F8F5E';
const LOSS_COLOR = 'rgba(255, 40, 57, 0.8)'; // #FF2839 @ 0.8 (design token: background.accent.sell)
const DARK_BG = '#111111';
const WHITE = '#FFFFFF';
const TRANSPARENT = 'rgba(0, 0, 0, 0)';

export type TpSlDragLineStyle = {
  lineColor: string;
  bodyBackgroundColor: string;
  bodyBorderColor: string;
  bodyTextColor: string;
  quantityBackgroundColor: string;
  quantityBorderColor: string;
  quantityTextColor: string;
};

const PROFIT_STYLE: TpSlDragLineStyle = {
  lineColor: PROFIT_COLOR,
  bodyBackgroundColor: DARK_BG,
  bodyBorderColor: PROFIT_COLOR,
  bodyTextColor: WHITE,
  quantityBackgroundColor: DARK_BG,
  quantityBorderColor: PROFIT_COLOR,
  quantityTextColor: WHITE,
};

const LOSS_STYLE: TpSlDragLineStyle = {
  lineColor: LOSS_COLOR,
  bodyBackgroundColor: DARK_BG, // TP와 동일하게 어두운 배경 + 빨강 외곽선
  bodyBorderColor: LOSS_COLOR,
  bodyTextColor: WHITE,
  quantityBackgroundColor: DARK_BG,
  quantityBorderColor: LOSS_COLOR,
  quantityTextColor: WHITE,
};

/**
 * 완전 투명 스타일 — 라인은 **상시 존재(드래그 가능)** 시키되 평시엔 보이지 않게 할 때 쓴다.
 * (라인을 remove하지 않으므로 "빠르게 잡아도 라인이 없어서 못 잡는" 레이스가 사라진다.)
 */
export const HIDDEN_DRAG_STYLE: TpSlDragLineStyle = {
  lineColor: TRANSPARENT,
  bodyBackgroundColor: TRANSPARENT,
  bodyBorderColor: TRANSPARENT,
  bodyTextColor: TRANSPARENT,
  quantityBackgroundColor: TRANSPARENT,
  quantityBorderColor: TRANSPARENT,
  quantityTextColor: TRANSPARENT,
};

/** 드래그 방향(TP/SL)에 따른 라인 스타일을 고른다. */
export function tpSlDragLineStyle(kind: TpSlDragIntent['kind']): TpSlDragLineStyle {
  return kind === 'SL' ? LOSS_STYLE : PROFIT_STYLE;
}

/**
 * 포지션 side에 따른 라인 스타일(Long=녹색, Short=빨강).
 * 호버/평시 핸들 라인처럼 손익 방향이 아직 없을 때 포지션 방향색으로 표시한다.
 */
export function tpSlDragLineStyleBySide(side: TpSlDragIntent['side']): TpSlDragLineStyle {
  return side === 'Short' ? LOSS_STYLE : PROFIT_STYLE;
}
